"use client";

import { useEffect, useMemo, useState } from "react";
import { fetchDoOrDumpReportNarrative } from "@/lib/do-or-dump-report-narrative-client";
import {
  reportNarrativeSnapshotFromDashboardPayload,
  type DoOrDumpReportNarrativeSnapshot,
} from "@/lib/do-or-dump-report-narrative-snapshot";
import type { DoOrDumpDashboardPayload } from "@/types/do-or-dump-dashboard";
import type { DoOrDumpReportNarrative } from "@/types/do-or-dump-report-narrative";

export function useDoOrDumpReportNarrative(
  payload: DoOrDumpDashboardPayload,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled !== false;
  const snapshot = useMemo(() => reportNarrativeSnapshotFromDashboardPayload(payload), [payload]);
  const [narrative, setNarrative] = useState<DoOrDumpReportNarrative | null>(null);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setNarrative(null);
      return;
    }
    const ac = new AbortController();
    let cancelled = false;
    setLoading(true);
    setNarrative(null);
    void (async () => {
      try {
        const n = await fetchDoOrDumpReportNarrative(snapshot, ac.signal);
        if (!cancelled) setNarrative(n);
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        if (!cancelled) {
          setNarrative(null);
          console.error("[useDoOrDumpReportNarrative]", e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [snapshot, enabled]);

  return { narrative, loading };
}

export function useDoOrDumpFunnelNarrative(snapshot: DoOrDumpReportNarrativeSnapshot | null) {
  const [narrative, setNarrative] = useState<DoOrDumpReportNarrative | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!snapshot) {
      setNarrative(null);
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    let cancelled = false;
    setLoading(true);
    setNarrative(null);
    void (async () => {
      try {
        const n = await fetchDoOrDumpReportNarrative(snapshot, ac.signal);
        if (!cancelled) setNarrative(n);
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        if (!cancelled) {
          setNarrative(null);
          console.error("[useDoOrDumpFunnelNarrative]", e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [snapshot]);

  return { narrative, loading };
}
